/**
 * One-shot "frame THIS feature group" requests for useZoomToActiveFeatureGroup.
 *
 * Normally the hook frames whatever partId the active dock slot's selector returns
 * (selectionState.materialsCanvasClass / navigationState.visualizerCanvasPartId). Some flows need
 * to frame a different group for a while — e.g. a wizard group with clickOnRoomLogic + clickOnLogic:
 * after the room is switched, the clickOnLogic feature is clicked programmatically and the canvas
 * should zoom to THAT feature's group, not to the wizard step's group (see
 * visComponents/navigation/roomSwitchingFunctions.ts -> zoomToClickOnLogicFeatureAfterRoomSwitch).
 *
 * Plain external store (same pattern as docking/dockStore.js), no Redux slice: nothing but the
 * hook reads it, and every request needs a fresh `id` so asking for the same partId twice still
 * re-frames.
 */

export interface FeatureZoomRequest {
  id: number;
  partId: string;
  requestedAt: number;
}

let value: FeatureZoomRequest | null = null;
let nextId = 1;
const listeners = new Set<() => void>();

const notify = () => listeners.forEach((listener) => listener());

const subscribe = (listener: () => void): (() => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

const getSnapshot = (): FeatureZoomRequest | null => value;

export const featureZoomRequestStore = { subscribe, getSnapshot };

/**
 * Asks the docked canvas to frame the feature group with `partId` (on the currently active slot,
 * if that slot does feature zoom at all). The override stays until the slot's own active partId
 * genuinely changes, the dock slot changes, or a newer request arrives — see the hook.
 */
export const requestZoomToFeatureGroup = (partId?: string | null): void => {
  if (!partId) {
    return;
  }
  value = { id: nextId++, partId, requestedAt: Date.now() };
  notify();
};

export default featureZoomRequestStore;
